import type { Difficulty, Extras, StreakMode } from "./constants";
import type { RngState } from "./math/rng";

export type Phase = "countdown" | "playing" | "over";

/** A lit spot. Lights are kept sorted by id (stable iteration order). */
export interface Light {
	/** Unique and increasing. */
	id: number;
	/** Index into SPOTS. */
	spot: number;
	/** Index into the view's palette (0 … LIGHT_COLOR_COUNT − 1). */
	color: number;
	golden: boolean;
	/** An Oogie Boogie light (only with Minuspoäng): tapping it costs points. */
	trick: boolean;
	/** The tick it switched on. */
	litTick: number;
	/** The tick it goes out unless collected first. */
	outTick: number;
}

/** A ghost out of hiding; `ghostPosition()` says where it is at a tick. */
export interface Ghost {
	id: number;
	/** Index into HIDES. */
	hide: number;
	startTick: number;
	endTick: number;
}

/** A bat crossing the sky (picture pixels); `batPosition()` says where it is at a tick. */
export interface Bat {
	id: number;
	startTick: number;
	endTick: number;
	fromX: number;
	toX: number;
	y: number;
}

/**
 * The whole game state: plain, serialisable data (structuredClone it for snapshots or
 * replays). Only `step()` changes it.
 */
export interface WorldState {
	tick: number;
	rng: RngState;
	phase: Phase;
	/** Round length in seconds (for the stars and the HUD). */
	seconds: number;
	difficulty: Difficulty;
	/** A tap that hits no light costs MISS_PENALTY points (Minuspoäng). */
	missPenalty: boolean;
	/** Hit streaks (Svit): off, or what breaks them. */
	streakMode: StreakMode;
	/** Mardröm: no time limit, a rising pace, and the round ends when the lives are gone. */
	nightmare: boolean;
	/** Lives left (Mardröm; 0 otherwise). */
	lives: number;
	/** The tick the first light may switch on (end of the countdown). */
	startTick: number;
	/** The tick the round ends. */
	endTick: number;
	nextId: number;
	/** The earliest tick the next light may switch on. */
	nextLightTick: number;
	lights: Light[];
	/** Per spot: the tick it may light up again. */
	restUntil: number[];
	score: number;
	collected: number;
	golden: number;
	/** Taps that hit no light while playing. */
	missed: number;
	/** Collects in a row (Svit on), and the longest this round. */
	streak: number;
	longestStreak: number;
	/** Lights (not trick lights) that went out uncollected: with `collected`, the hit rate. */
	expired: number;
	/** Which bonus visitors this round has. */
	extras: Extras;
	/** The bat in the sky, if any, and the earliest tick for the next one. */
	bat: Bat | null;
	nextBatTick: number;
	batsCaught: number;
	/** The ghost out of hiding, if any, and the earliest tick for the next one. */
	ghost: Ghost | null;
	nextGhostTick: number;
	ghostsCaught: number;
	/** Trick lights tapped. */
	tricked: number;
}

/** The menu's choices besides the round length. */
export interface RoundOptions {
	difficulty?: Difficulty;
	missPenalty?: boolean;
	streakMode?: StreakMode;
	extras?: Extras;
	nightmare?: boolean;
}

/** A tap or click on the backdrop, in backdrop pixels. */
export interface Tap {
	x: number;
	y: number;
	/** Extra distance a tap may be away from a spot's edge (bigger on small screens). */
	reach: number;
}

/** One tick of player input: the taps since the last tick. */
export interface ControlState {
	taps: readonly Tap[];
}

export const NO_CONTROLS: Readonly<ControlState> = Object.freeze({ taps: Object.freeze([]) });

/** What happened during a step, for views and audio (the sim never touches either). */
export type SimEvent =
	| { type: "started" }
	| { type: "lit"; light: Light }
	| { type: "collected"; light: Light; points: number; score: number }
	| { type: "expired"; light: Light }
	/** A streak of at least one ended (Svit on). */
	| { type: "streakBroken"; streak: number }
	/** A tap that hit nothing with Minuspoäng on; `penalty` is what it cost (0 at score 0). */
	| { type: "missed"; tap: Tap; penalty: number; score: number }
	/** A trick light tapped; `penalty` is what it cost (never below 0). */
	| { type: "tricked"; light: Light; penalty: number; score: number }
	/** Every STREAK_EVERY collects in a row (Svit on); the bonus grows with the streak. */
	| { type: "streak"; count: number; bonus: number; score: number }
	| { type: "batCame"; bat: Bat }
	| { type: "batCaught"; bat: Bat; x: number; y: number; points: number; score: number }
	| { type: "batGone"; bat: Bat }
	| { type: "ghostCame"; ghost: Ghost }
	| { type: "ghostCaught"; ghost: Ghost; x: number; y: number; points: number; score: number }
	| { type: "ghostGone"; ghost: Ghost }
	/** Mardröm: a miss, a trick light or a light going out cost a life. */
	| { type: "lifeLost"; lives: number }
	/** The round is over: the time ran out, or (Mardröm) the lives did. */
	| { type: "timeUp"; score: number };
